import isFeatureAllowed from "../../../util/is-feature-allowed";
import { hasFeature } from "../../../util/plan-features";
import dateRange, { defaultRangeFor } from "../date-range";
import isUndefined from "lodash.isundefined";
import prettyPrintKey from "../../../scripts/pretty-print-key";
import ReadingCharts from "../charts/reading-charts";
import getSensorReadings from "../../../scripts/get-sensor-readings";
import DashboardCurrentReadings from "../current-readings";
import sensorSchedule from "./sensor-schedule";
import { renderResponseDetails } from "./sensor-response";
import { setParams } from "../../../scripts/url-state";
import { copyLinkButton } from "../../../components/copy-link";

const PLACES = { indoor: 'Indoors', unheated: 'Unheated', outdoor: 'Outdoors', vehicle: 'Vehicle' };
const escHtml = v => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

// where the sensor is, as the enterprise set it (and the owner, when theirs shows)
const setupLine = sensor => {
  const o = sensor._ours;
  const zip = (o && o.zip) || String(sensor.zip_code || '').trim();
  const place = (o && o.placement) || sensor.placement || '';
  const bits = [
    zip ? `Zip ${escHtml(zip)}${o && o.zip ? '' : ' (owner’s)'}` : 'No zip code',
    place ? `${escHtml(PLACES[place] || place)}${o && o.placement ? '' : ' (owner’s)'}` : '',
    o && o.category ? escHtml(o.category) : '',
  ].filter(Boolean);
  return `<p class="dashboard-active-sensor__setup">${bits.join(' · ')}${o && o.notes ? `<br><small>${escHtml(o.notes)}</small>` : ''}</p>`;
};

const renderActiveSensor = async (dashboard, sensorId, activeChart=null) => {

  dashboard.autoChartType = activeChart;

  if(!dashboard.sensors || !dashboard.sensors[sensorId]) {
    return;
  }

  if(dashboard.showingActiveSensor) {
    $(`.data-grid-row[data-sensor="${dashboard.currentSensor.sensor_id}"]`).removeClass("is-selected");
  }

  dashboard.currentSensor = dashboard.sensors[sensorId]

  if(dashboard.showingActiveSensor) {
    $(`.data-grid-row[data-sensor="${dashboard.currentSensor.sensor_id}"]`).addClass("is-selected");
    setParams({ sensor: dashboard.currentSensor.sensor_id });
  }

  const sensor = dashboard.sensors[sensorId];

  let html = `
    <h2 style="font-size: 18px;"><span style="font-size:10px;">ID: </span>${sensor.sensor_name} <span style="font-size:14px;">(${sensor.sensor_id})</span></h2>
    `;

  // a link that opens this sensor beside its account's grid
  const accountId = dashboard.getSensorAccountId(sensor);
  const sensorLink = `/dashboard?${accountId ? `account=${accountId}&` : ''}sensor=${encodeURIComponent(sensor.sensor_id)}`;

  html += `
    <div class="margin--medium dashboard-active-sensor__actions">
      ${isFeatureAllowed(window.USER_ROLES.ACCOUNT_MANAGER) && hasFeature('account_login') ? `
      <button data-action="view_sensor" data-sensor="${sensor.sensor_id}" data-user="${sensor.owner_id}" class="button button--small">View Sensor in Account</button>` : ''}
      ${dashboard.canSetup() ? `<button type="button" class="button button--small" data-action="set_location_one" data-sensor="${sensor.sensor_id}">Set location</button>` : ''}
      ${copyLinkButton(sensorLink)}
    </div>
    ${setupLine(sensor)}
    `

  html += `
    <div id="active-sensor-scrollview" style="overflow: auto;">
    
    <div class="dashboard-active-sensor__response">
      <h3>Alert Response</h3>
      ${renderResponseDetails(sensor)}
    </div>
    <div class="dashboard-active-sensor__date-range">${dateRange(defaultRangeFor(sensor))}</div>
    <div class="dashboard-active-sensor__current-readings"></div>
    ${sensorSchedule(sensor)}
    <div class="dashboard-active-sensor__charts"></div>
    `;

  if(dashboard.currentSensor.alerts.length > 0) {

    html += `<div class="dashboard-active-sensor__alerts">`
    html += '<h3>Alerts</h3>';

    html += '<table cellpadding="0" cellspacing="0"><tr><th>Name</th><th>Condition</th><th>Last Triggered</th></tr>'

    dashboard.currentSensor.alerts.sort((a, b) => {
      const aTime = a.last_triggered ? new Date(a.last_triggered).getTime() : 0;
      const bTime = b.last_triggered ? new Date(b.last_triggered).getTime() : 0;
      return bTime - aTime;
    });

    for(let alert of dashboard.currentSensor.alerts) {

      let condition;
      let value = !isUndefined(alert.cond_val) ? alert.cond_val : alert.reading_value;

      value = dashboard.getDisplayReadingValue(alert.reading_type, value);

      switch(alert.reading_type) {
        case 'dew_point':
          condition = `Dew point within ${celsiusUnitsToUserTemp(value, 1)}&deg;${window.getUserTempPref()}`;
          break;
        default:
          condition = `${prettyPrintKey(alert.reading_type)} ${alert.reading_condition} ${value}`;
          break;
      }

      // your alert, adjusted for this account (Manage Account -> Alerts):
      // a small note, the alert's own value on hover
      if(Number(alert.account_value) === 1) {
        const usual = alert.reading_type === 'dew_point'
          ? `${celsiusUnitsToUserTemp(alert.enterprise_value, 1)}&deg;${window.getUserTempPref()}`
          : dashboard.getDisplayReadingValue(alert.reading_type, alert.enterprise_value);
        condition += ` <small class="dashboard-active-sensor__alert__own" title="Adjusted for this account from ${usual}">this account</small>`;
      }

      html += `
        <tr class="dashboard-active-sensor__alert">
          <td class="dashboard-active-sensor__alert__name">${alert.alert_name}</td>
          <td class="dashboard-active-sensor__alert__condition">${condition}</td>
          <td class="dashboard-active-sensor__alert__last-triggered" title="${alert.last_triggered ? window.getUserTimezoneDate(alert.last_triggered).format('MMM DD, YYYY h:mm a') : "Never triggered"}">
            ${alert.last_triggered ? window.getPrettyTimeAgo(alert.last_triggered) : "Never"}
          </td>
        </tr>
        `
    }

    html += '</table>'
    html += `</div>`

  }

  html += '</div>';

  dashboard.$activeSensorContainer.html(html);
  dashboard.$dateSelect = $('.dashboard-active-sensor .dashboard-active-sensor__readings-date-range');
  dashboard.$customDateSelect = dashboard.$activeSensorContainer.find('.dashboard-active-sensor__readings-date-range__custom');
  dashboard.$customDateSelectError = dashboard.$activeSensorContainer.find('.dashboard-active-sensor__readings-date-range__custom__error');
  dashboard.$dateSelectStart = dashboard.$activeSensorContainer.find('.custom-date-range-start');
  dashboard.$dateSelectEnd = dashboard.$activeSensorContainer.find('.custom-date-range-end');

  dashboard.chartsContainer = $('.dashboard-active-sensor__charts')[0];
  dashboard.ReadingCharts = new ReadingCharts(dashboard.chartsContainer);

  const readings = await getSensorReadings(sensorId, dashboard.$dateSelect.val());

  dashboard.$currentReadingsContainer = $('.dashboard-active-sensor__current-readings');
  dashboard.DashboardCurrentReadings = new DashboardCurrentReadings(dashboard.$currentReadingsContainer);

  dashboard.DashboardCurrentReadings.render(sensor);
  dashboard.DashboardCurrentReadings.update(sensor, readings);

  let dataFor = null;

  // show/hide charts
  if(isUndefined(sensor.last_tcTemp)) {
    dashboard.$activeSensorContainer.find(`.reading-charts-tab[data-for="tcTemp"]`).hide();
    dataFor = "temperature";
  } else {
    dashboard.$activeSensorContainer.find(`.reading-charts-tab[data-for="tcTemp"]`).show();
    dataFor = "tcTemp";
  }

  dashboard.selectChart(activeChart ? activeChart : dataFor);

  dashboard.updateDateSelect();
  dashboard.maybeShowReadings(readings);

  console.log(sensor);
  
}

export default renderActiveSensor;
